import { baseApi } from "api/BaseApi";
import { ProficiencyFeedback } from "api/memberPortal/proficiencyFeedback/proficiencyFeedback.types";

import {
  DraftFeedbackPayload,
  FeedbackQueryParams,
  FinalFeedbackPayload,
  TransformedProficiencyFeedback,
} from "./feedback.types";

const PRODUCT_BASE_QUERY = "/v2/products";

const feedbackApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // GET
    getProductFeedback: build.query<
      TransformedProficiencyFeedback,
      FeedbackQueryParams
    >({
      query: ({ parentId }) => ({
        url: `${PRODUCT_BASE_QUERY}/${parentId}/feedback/`,
      }),
      providesTags: (_result, _error, { parentId }) => [
        { type: "PROFICIENCY_FEEDBACK", id: parentId },
      ],
      transformResponse: (response: ProficiencyFeedback) => ({
        ...response,
        // Coerce nulls → undefined for nullable boolean fields
        validationTeamTwoWeeks: response.validationTeamTwoWeeks ?? undefined,
        validationTeamDays: response.validationTeamDays?.toString() ?? null,
        missTds: response.missTds ?? undefined,
        misinterpretPolicy: response.misinterpretPolicy ?? undefined,
        performTests: response.performTests ?? undefined,
        // Coerce null/empty enum-like fields → undefined
        quality: response.quality || undefined,
        discrepancies: response.discrepancies || undefined,
        evidence: response.evidence || undefined,
        clarification: response.clarification || undefined,
        missingItems: response.missingItems || undefined,
      }),
    }),
    // Update/Create
    saveProductFeedback: build.mutation<
      ProficiencyFeedback,
      DraftFeedbackPayload
    >({
      query: ({ parentId, body }) => ({
        url: `${PRODUCT_BASE_QUERY}/${parentId}/feedback/draft/`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { parentId }) => [
        { type: "PROFICIENCY_FEEDBACK", parentId },
      ],
    }),
    // Finalize
    finalizeProductFeedback: build.mutation<
      ProficiencyFeedback,
      FinalFeedbackPayload
    >({
      query: ({ parentId, body }) => ({
        url: `${PRODUCT_BASE_QUERY}/${parentId}/feedback/finalize/`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { parentId }) => [
        { type: "PROFICIENCY_FEEDBACK", parentId },
      ],
    }),
    // Withdraw (finalize with withdrawn flag)
    withdrawProductFeedback: build.mutation<
      ProficiencyFeedback,
      FinalFeedbackPayload
    >({
      query: ({ parentId, body }) => ({
        url: `${PRODUCT_BASE_QUERY}/${parentId}/feedback/finalize/?withdrawn=true`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { parentId }) => [
        { type: "PROFICIENCY_FEEDBACK", parentId },
      ],
    }),
  }),
});

export const {
  useGetProductFeedbackQuery,
  useSaveProductFeedbackMutation,
  useFinalizeProductFeedbackMutation,
  useWithdrawProductFeedbackMutation,
} = feedbackApi;
